import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation } from "convex/react";
import { ChevronLeft } from "lucide-react";
import { api } from "../../convex/_generated/api";
import type { FlowAnswers } from "./types";
import {
  ACTIVITY_OPTIONS,
  EATING_OPTIONS,
  EXERCISE_OPTIONS,
  EXERCISE_TYPE_OPTIONS,
  GENDER_OPTIONS,
  GOAL_OPTIONS,
  IMPACT_OPTIONS,
  LOGGING_OPTIONS,
  MOTIVATION_OPTIONS,
  PACE_OPTIONS,
  STEPS_BUCKET_MIDPOINTS,
  STEPS_OPTIONS,
  stepsBucketForValue,
} from "./types";
import { computePlan } from "./plan";
import type { OnboardingGoal, OnboardingPace } from "./plan";
import { OptionScreen } from "./components/OptionScreen";
import { SimpleNumberStep, WeightStep } from "./components/NumberSteps";
import { HeightStep } from "./components/HeightStep";
import { SafetyStep } from "./components/SafetyStep";
import { MeasurementsStep } from "./components/MeasurementsStep";
import { SkipSheet } from "./components/SkipSheet";
import { WelcomeScreen } from "./screens/WelcomeScreen";
import {
  AccuracyScreen,
  BenefitOneScreen,
  BenefitTwoScreen,
  SocialProofScreen,
} from "./screens/Interstitials";
import { BuildingPlanScreen } from "./screens/BuildingPlanScreen";
import { ResultsScreen } from "./screens/ResultsScreen";
import { PaywallGate } from "./screens/PaywallGate";

export type PremiumStatus = "trial" | "free";

type StepId =
  | "welcome"
  | "goal"
  | "motivation"
  | "impact"
  | "benefit1"
  | "currentWeight"
  | "goalWeight"
  | "height"
  | "age"
  | "gender"
  | "activity"
  | "exercise"
  | "exerciseType"
  | "steps"
  | "safety"
  | "measurements"
  | "benefit2"
  | "pace"
  | "logging"
  | "eating"
  | "accuracy"
  | "building"
  | "results"
  | "social"
  | "paywall";

interface StepDef {
  readonly id: StepId;
  readonly inputBearing: boolean;
  readonly hidden?: (answers: FlowAnswers) => boolean;
}

const STEP_DEFS: readonly StepDef[] = [
  { id: "welcome", inputBearing: false },
  { id: "goal", inputBearing: true },
  { id: "motivation", inputBearing: true },
  { id: "impact", inputBearing: true },
  { id: "benefit1", inputBearing: false },
  { id: "currentWeight", inputBearing: true },
  {
    id: "goalWeight",
    inputBearing: true,
    hidden: (a) => a.goalType === "maintain" || a.goalType === "healthier",
  },
  { id: "height", inputBearing: true },
  { id: "age", inputBearing: true },
  { id: "gender", inputBearing: true },
  { id: "activity", inputBearing: true },
  { id: "exercise", inputBearing: true },
  { id: "exerciseType", inputBearing: true },
  { id: "steps", inputBearing: true },
  { id: "safety", inputBearing: true },
  { id: "measurements", inputBearing: true },
  { id: "benefit2", inputBearing: false },
  {
    id: "pace",
    inputBearing: true,
    hidden: (a) => a.goalType !== "lose" && a.goalType !== "gain" && a.goalType !== "muscle",
  },
  { id: "logging", inputBearing: true },
  { id: "eating", inputBearing: true },
  { id: "accuracy", inputBearing: false },
  { id: "building", inputBearing: false },
  { id: "results", inputBearing: false },
  { id: "social", inputBearing: false },
  { id: "paywall", inputBearing: false },
];

const SKIP_HIDDEN_STEPS: ReadonlySet<StepId> = new Set(["welcome", "building", "paywall"]);

const FALLBACK_KCAL = 2000;
const WEEK_MS = 7 * 86_400_000;

export function OnboardingFlow() {
  const [answers, setAnswers] = useState<FlowAnswers>({ unitWeight: "kg" });
  const [stepIndex, setStepIndex] = useState(0);
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [skipOpen, setSkipOpen] = useState(false);
  const [progressRevealed, setProgressRevealed] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completionError, setCompletionError] = useState<string | null>(null);
  const lastAttempt = useRef<{ status: PremiumStatus; source: string } | null>(null);

  const steps = useMemo(
    () => STEP_DEFS.filter((step) => !step.hidden || !step.hidden(answers)),
    [answers],
  );
  const clampedIndex = Math.min(stepIndex, steps.length - 1);
  const currentStep = steps[clampedIndex];

  const completeOnboarding = useMutation(api.profile.completeOnboarding).withOptimisticUpdate(
    (localStore, args) => {
      const current = localStore.getQuery(api.profile.getProfile, {});
      if (current !== undefined && current !== null) {
        localStore.setQuery(api.profile.getProfile, {}, {
          ...current,
          ...args,
          onboardingComplete: true,
        });
      }
    },
  );

  const setAnswer = useCallback(<K extends keyof FlowAnswers>(key: K, value: FlowAnswers[K]) => {
    setAnswers((prev) => {
      const next = { ...prev, [key]: value };
      // Goals without a weight delta auto-set a steady pace (pace step is hidden).
      if (key === "goalType") {
        const goal = value as OnboardingGoal | undefined;
        if (goal === "maintain" || goal === "healthier") {
          next.weightLossPace = "steady";
        }
      }
      return next;
    });
  }, []);

  const goNext = useCallback(() => {
    if (clampedIndex >= steps.length - 1) return;
    const from = steps[clampedIndex].id;
    const to = steps[clampedIndex + 1].id;
    console.log("[Frontend][OnboardingFlow] Step transition", { from, to });
    if (from === "currentWeight") {
      // Maintain/healthier goals skip the goal-weight step; mirror current weight.
      setAnswers((prev) =>
        prev.goalType === "maintain" || prev.goalType === "healthier"
          ? { ...prev, targetWeightKg: prev.weightKg }
          : prev,
      );
    }
    setDirection("forward");
    setStepIndex(clampedIndex + 1);
  }, [clampedIndex, steps]);

  const goBack = useCallback(() => {
    if (clampedIndex <= 0) return;
    console.log("[Frontend][OnboardingFlow] Step transition", {
      from: steps[clampedIndex].id,
      to: steps[clampedIndex - 1].id,
    });
    setDirection("back");
    setStepIndex(clampedIndex - 1);
  }, [clampedIndex, steps]);

  const complete = useCallback(
    async (status: PremiumStatus, source: string) => {
      lastAttempt.current = { status, source };
      setCompleting(true);
      setCompletionError(null);

      const plan = computePlan({
        goalType: answers.goalType,
        weightKg: answers.weightKg,
        targetWeightKg: answers.targetWeightKg,
        heightCm: answers.heightCm,
        age: answers.age,
        gender: answers.gender,
        activityLevel: answers.activityLevel,
        exerciseFrequency: answers.exerciseFrequency,
        exerciseType: answers.exerciseType,
        avgDailySteps: answers.avgDailySteps,
        weightLossPace: answers.weightLossPace,
        eatingStyle: answers.eatingStyle,
        bodyFatPercent: answers.bodyFatPercent,
        waistCm: answers.waistCm,
        safetyFlags: answers.safetyFlags,
      });
      console.log("[Frontend][OnboardingFlow] Plan computed", {
        dailyKcal: plan.dailyKcal,
        weeksToGoal: plan.weeksToGoal,
        restricted: plan.restricted === true,
        source,
      });

      const hasBiometrics =
        answers.weightKg !== undefined && answers.heightCm !== undefined && answers.age !== undefined;

      const payload = {
        dailyGoalKcal: hasBiometrics ? plan.dailyKcal : FALLBACK_KCAL,
        dailyProteinG: plan.proteinG,
        dailyCarbsG: plan.carbsG,
        dailyFatG: plan.fatG,
        dailyFiberG: plan.fiberG,
        dietType: hasBiometrics ? plan.dietType : ("balanced" as const),
        premiumStatus: status,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone ?? "auto",
        ...(answers.weightKg !== undefined
          ? {
              weightKg: answers.weightKg,
              startingWeightKg: answers.weightKg,
              unitWeight: answers.unitWeight,
            }
          : {}),
        ...(answers.targetWeightKg !== undefined ? { targetWeightKg: answers.targetWeightKg } : {}),
        ...(answers.heightCm !== undefined ? { heightCm: answers.heightCm } : {}),
        ...(answers.age !== undefined ? { age: answers.age } : {}),
        ...(answers.gender !== undefined ? { gender: answers.gender } : {}),
        ...(answers.activityLevel !== undefined ? { activityLevel: answers.activityLevel } : {}),
        ...(answers.goalType !== undefined ? { goalType: answers.goalType } : {}),
        ...(answers.motivation !== undefined ? { motivation: answers.motivation } : {}),
        ...(answers.impact !== undefined ? { impact: answers.impact } : {}),
        ...(answers.exerciseFrequency !== undefined
          ? { exerciseFrequency: answers.exerciseFrequency }
          : {}),
        ...(answers.exerciseType !== undefined ? { exerciseType: answers.exerciseType } : {}),
        ...(answers.avgDailySteps !== undefined ? { avgDailySteps: answers.avgDailySteps } : {}),
        ...(answers.bodyFatPercent !== undefined
          ? { bodyFatPercent: answers.bodyFatPercent }
          : {}),
        ...(answers.waistCm !== undefined ? { waistCm: answers.waistCm } : {}),
        ...(answers.safetyFlags !== undefined ? { safetyFlags: answers.safetyFlags } : {}),
        ...(answers.weightLossPace !== undefined
          ? { weightLossPace: answers.weightLossPace }
          : {}),
        ...(answers.loggingStyle !== undefined ? { loggingStyle: answers.loggingStyle } : {}),
        ...(answers.eatingStyle !== undefined ? { eatingStyle: answers.eatingStyle } : {}),
        ...(plan.weeksToGoal > 0 ? { targetDate: Date.now() + plan.weeksToGoal * WEEK_MS } : {}),
      };

      try {
        await completeOnboarding(payload);
      } catch (err) {
        console.error("[Frontend][Onboarding] Completion failed", err);
        setCompletionError("We couldn't save your plan. Check your connection and try again.");
      } finally {
        setCompleting(false);
      }
    },
    [answers, completeOnboarding],
  );

  const requestSkip = useCallback(() => {
    console.log("[Frontend][OnboardingFlow] Skip requested", { step: currentStep.id });
    setSkipOpen(true);
  }, [currentStep.id]);

  const confirmSkip = useCallback(() => {
    console.log("[Frontend][OnboardingFlow] Skip confirmed", { step: currentStep.id });
    setSkipOpen(false);
    void complete("free", "skip");
  }, [complete, currentStep.id]);

  const dismissSkip = useCallback(() => {
    console.log("[Frontend][OnboardingFlow] Skip dismissed, progress revealed", {
      step: currentStep.id,
    });
    setSkipOpen(false);
    setProgressRevealed(true);
  }, [currentStep.id]);

  const handlePaywallChoice = useCallback(
    (status: PremiumStatus, choice: string) => {
      console.log("[Frontend][OnboardingFlow] Paywall choice", { choice, status });
      void complete(status, choice);
    },
    [complete],
  );

  const inputStepCount = steps.filter((step) => step.inputBearing).length;
  const completedInputCount = steps
    .slice(0, clampedIndex)
    .filter((step) => step.inputBearing).length;
  const progressFraction = inputStepCount > 0 ? completedInputCount / inputStepCount : 0;

  const goalWeightWarning = (candidateKg: number): string | null => {
    if (answers.goalType === "lose" && answers.weightKg !== undefined && candidateKg >= answers.weightKg) {
      return "Choose a target below your current weight.";
    }
    if (
      (answers.goalType === "gain" || answers.goalType === "muscle") &&
      answers.weightKg !== undefined &&
      candidateKg <= answers.weightKg
    ) {
      return "Choose a target above your current weight.";
    }
    return null;
  };

  const renderStep = () => {
    switch (currentStep.id) {
      case "welcome":
        return <WelcomeScreen onStart={goNext} />;
      case "goal":
        return (
          <OptionScreen
            title="What's your main goal?"
            options={GOAL_OPTIONS}
            value={answers.goalType}
            primaryValue="lose"
            onSelect={(v) => setAnswer("goalType", v as OnboardingGoal)}
            onAdvance={goNext}
          />
        );
      case "motivation":
        return (
          <OptionScreen
            title="Why do you want to reach this goal?"
            options={MOTIVATION_OPTIONS}
            value={answers.motivation}
            onSelect={(v) => setAnswer("motivation", v)}
            onAdvance={goNext}
          />
        );
      case "impact":
        return (
          <OptionScreen
            title="What would reaching your goal change most for you?"
            options={IMPACT_OPTIONS}
            value={answers.impact}
            onSelect={(v) => setAnswer("impact", v)}
            onAdvance={goNext}
          />
        );
      case "benefit1":
        return <BenefitOneScreen answers={answers} onContinue={goNext} />;
      case "currentWeight":
        return (
          <WeightStep
            title="What's your current weight?"
            valueKg={answers.weightKg}
            unit={answers.unitWeight}
            onUnitChange={(u) => setAnswer("unitWeight", u)}
            onContinue={(kg) => {
              setAnswer("weightKg", kg);
              goNext();
            }}
          />
        );
      case "goalWeight":
        return (
          <WeightStep
            title="What weight are you aiming for?"
            helperText="You can always change this later."
            valueKg={answers.targetWeightKg}
            unit={answers.unitWeight}
            onUnitChange={(u) => setAnswer("unitWeight", u)}
            warningFor={goalWeightWarning}
            onContinue={(kg) => {
              setAnswer("targetWeightKg", kg);
              goNext();
            }}
          />
        );
      case "height":
        return (
          <HeightStep
            title="How tall are you?"
            valueCm={answers.heightCm}
            unit={answers.unitHeight ?? "cm"}
            onUnitChange={(u) => setAnswer("unitHeight", u)}
            weightKg={answers.weightKg}
            onContinue={(cm) => {
              setAnswer("heightCm", cm);
              goNext();
            }}
          />
        );
      case "age":
        return (
          <SimpleNumberStep
            title="How old are you?"
            unitLabel="years"
            placeholder="30"
            min={13}
            max={100}
            value={answers.age}
            onContinue={(v) => {
              setAnswer("age", v);
              goNext();
            }}
          />
        );
      case "gender":
        return (
          <OptionScreen
            title="Which option best describes you?"
            subtext="Used only to estimate your calorie needs."
            options={GENDER_OPTIONS}
            value={answers.gender}
            onSelect={(v) => setAnswer("gender", v)}
            onAdvance={goNext}
          />
        );
      case "activity":
        return (
          <OptionScreen
            title="How active are you on most days?"
            options={ACTIVITY_OPTIONS}
            value={answers.activityLevel}
            onSelect={(v) => setAnswer("activityLevel", v)}
            onAdvance={goNext}
          />
        );
      case "exercise":
        return (
          <OptionScreen
            title="How often do you intentionally exercise?"
            options={EXERCISE_OPTIONS}
            value={answers.exerciseFrequency}
            onSelect={(v) => setAnswer("exerciseFrequency", v)}
            onAdvance={goNext}
          />
        );
      case "exerciseType":
        return (
          <OptionScreen
            title="What kind of exercise do you do most?"
            options={EXERCISE_TYPE_OPTIONS}
            value={answers.exerciseType}
            onSelect={(v) => setAnswer("exerciseType", v)}
            onAdvance={goNext}
          />
        );
      case "steps":
        return (
          <OptionScreen
            title="On an average day, how many steps do you take?"
            subtext="A rough guess is fine."
            options={STEPS_OPTIONS}
            value={
              answers.avgDailySteps !== undefined
                ? stepsBucketForValue(answers.avgDailySteps)
                : undefined
            }
            onSelect={(v) => setAnswer("avgDailySteps", STEPS_BUCKET_MIDPOINTS[v])}
            onAdvance={goNext}
          />
        );
      case "safety":
        return (
          <SafetyStep
            selected={
              answers.safetyFlags ??
              (answers.age !== undefined && answers.age < 18 ? ["under_18"] : [])
            }
            onContinue={(flags) => {
              setAnswer("safetyFlags", flags);
              goNext();
            }}
          />
        );
      case "measurements":
        return (
          <MeasurementsStep
            bodyFatPercent={answers.bodyFatPercent}
            waistCm={answers.waistCm}
            onContinue={({ bodyFatPercent, waistCm }) => {
              setAnswers((prev) => ({ ...prev, bodyFatPercent, waistCm }));
              goNext();
            }}
          />
        );
      case "benefit2":
        return <BenefitTwoScreen answers={answers} onContinue={goNext} />;
      case "pace":
        return (
          <OptionScreen
            title={
              answers.goalType === "lose"
                ? "How fast do you want to lose weight?"
                : "How fast do you want to gain?"
            }
            options={PACE_OPTIONS}
            value={answers.weightLossPace}
            onSelect={(v) => setAnswer("weightLossPace", v as OnboardingPace)}
            onAdvance={goNext}
          />
        );
      case "logging":
        return (
          <OptionScreen
            title="How do you want to track food?"
            options={LOGGING_OPTIONS}
            value={answers.loggingStyle}
            onSelect={(v) => setAnswer("loggingStyle", v)}
            onAdvance={goNext}
          />
        );
      case "eating":
        return (
          <OptionScreen
            title="Do you follow any eating style?"
            options={EATING_OPTIONS}
            value={answers.eatingStyle}
            onSelect={(v) => setAnswer("eatingStyle", v)}
            onAdvance={goNext}
          />
        );
      case "accuracy":
        return <AccuracyScreen onContinue={goNext} />;
      case "building":
        return <BuildingPlanScreen answers={answers} onDone={goNext} />;
      case "results":
        return <ResultsScreen answers={answers} onContinue={goNext} />;
      case "social":
        return <SocialProofScreen onContinue={goNext} />;
      case "paywall":
        return <PaywallGate completing={completing} onChoose={handlePaywallChoice} />;
    }
  };

  const showTopBar = currentStep.id !== "welcome" && currentStep.id !== "paywall";

  return (
    <div
      data-testid="onboarding-card"
      className="flex min-h-dvh w-full flex-col bg-muted pt-[env(safe-area-inset-top)]"
    >
      <div className="mx-auto flex w-full max-w-xl flex-1 flex-col px-4">
        {showTopBar && (
          <div className="flex items-center gap-3 py-3">
            <button
              type="button"
              aria-label="Back"
              onClick={goBack}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-text select-none transition-all duration-200 ease-out active:scale-[0.98] active:opacity-80"
            >
              <ChevronLeft size={22} aria-hidden="true" />
            </button>
            {progressRevealed ? (
              <div
                data-testid="onboarding-progress"
                className="h-1.5 flex-1 overflow-hidden rounded-full bg-border"
              >
                <div
                  className="h-full w-full origin-left rounded-full bg-accent transition-transform duration-300 ease-out"
                  style={{ transform: `scaleX(${progressFraction})` }}
                />
              </div>
            ) : (
              <div className="flex-1" aria-hidden="true" />
            )}
            {SKIP_HIDDEN_STEPS.has(currentStep.id) ? (
              <span className="min-w-[44px]" aria-hidden="true" />
            ) : (
              <button
                type="button"
                onClick={requestSkip}
                className="min-h-[44px] select-none px-2 text-sm text-text-muted/60 transition-opacity active:opacity-80"
              >
                Skip
              </button>
            )}
          </div>
        )}

        <div
          key={currentStep.id}
          className={`flex flex-1 flex-col ${direction === "forward" ? "ob-screen-enter" : ""}`}
        >
          {renderStep()}
        </div>
      </div>

      {completionError && (
        <div className="fixed inset-x-0 bottom-0 z-40 pb-[env(safe-area-inset-bottom)]">
          <div className="mx-auto flex max-w-xl items-center gap-3 rounded-t-2xl border border-border bg-surface px-4 py-4 shadow-sm">
            <p role="alert" className="flex-1 text-sm text-danger">
              {completionError}
            </p>
            <button
              type="button"
              onClick={() => {
                const attempt = lastAttempt.current;
                if (attempt) void complete(attempt.status, attempt.source);
              }}
              disabled={completing}
              className="min-h-[44px] select-none rounded-xl bg-accent px-4 text-sm font-semibold text-white transition-all duration-200 ease-out active:scale-[0.98]"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      <SkipSheet
        open={skipOpen}
        onKeep={dismissSkip}
        onSkipAnyway={confirmSkip}
      />
    </div>
  );
}
